<template>
  <div class="title-block">
    <i18n-t keypath="app.modals.deactivateParticipant.subtitle" :plural="data.length">
      <template #talent>
        <span v-safe-html="candidateWording" />
      </template>
    </i18n-t>
  </div>
  <p class="under-text">
    {{ $t("app.modals.deactivateParticipant.text", { count: data.length }) }}
  </p>
  <div class="btn-container">
    <Button class="secondary outline sm" :label="$t('app.modals.deactivateParticipant.buttons.cancel')" @click="emits('close')" />
    <Button class="primary sm" :label="$t('app.modals.deactivateParticipant.buttons.confirm', { count: data.length })" :loading="state.btnIsLoading" @click="desactivateParticipants" />
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getError } from "@/composables/error.js";
import { useDesactivateParticipations } from "@/queries/participant.query";
import { Participant } from "@/types/participant";

const toast = useToast();
const route = useRoute();
const { t } = useI18n();

const { mutateAsync: desactivateParticipations } = useDesactivateParticipations();

const emits = defineEmits(["close", "confirm", "confirmBulk"]);

const state = reactive({
  btnIsLoading: false,
});

const props = defineProps<{
  data: Participant[]
}>();

const candidateWording = computed(() => {
  let candidate;

  if (props.data[0]?.candidate_id as string) {
    candidate = `${props.data[0].candidate.first_name} ${props.data[0].candidate.last_name}`;
  } else if (props.data[0].pending_first_name && props.data[0].pending_last_name) {
    candidate = `${props.data[0].pending_first_name} ${props.data[0].pending_last_name}`;
  } else {
    candidate = props.data[0].pending_email;
  }

  return `<span class="primary-color bold">${candidate}</span>`;
});

async function desactivateParticipants() {
  state.btnIsLoading = true;

  await desactivateParticipations({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.spaceId as string,
      campaignId: route.params.campaignId as string,
    },
    participantIds: props.data.map((elt: Participant) => elt.participant_id),
  },
  {
    onSuccess: (resp) => { 
      toast.success(t("app.modals.deactivateParticipant.toasts.success", { count: props.data.length }));
      if (props.data.length > 1) emits("confirmBulk");
    },
    onError: (error) => {
      toast.error(error);
    }
  });

  emits("close");

  state.btnIsLoading = true;
}
</script>
